import './ReportRunConfigSummaryStep.scss';
import React, { ReactNode, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import hashObject from 'object-hash';
import classnames from 'classnames';
import { ByzzerButton, WithUid, WizardContext, WizardStepMessageOptions } from '@byzzer/ui-components';
import ByzzerInput from '@/components/form/ByzzerInput';
import ReportRunConfigSummary from '@/components/ConfigurationEditors/ReportConfigurationEditor/ReportRunConfigSummaryStep/ReportRunConfigSummary';
import { ReportRunConfig } from '@/types/ReportRun';
import { useTenantApi } from '@/hooks/useTenantApi';
import { ReportRunConfigWizardContext } from '../ReportRunConfigWizard/ReportRunConfigWizardContext';
import { WizardActions, WizardContent, WizardHeader, WizardStep } from '@byzzer/ui-components';
import { ConfigOptionsType, RunConfigOptions } from '@/types/RunConfigOptions';
import { ReportRunValidationError, useReportRunService } from '@/services/reportRun.service';
import { ErrorWithCodeResponse, ReportRunValidationResult } from '@/types/ApiTypes';
import {
    BEYOND_THREE_YEARS_DATA_WARNING_MESSAGE,
    DEFAULT_REPORT_VALIDATION_RESULT_WARNING_MESSAGE,
    DUPLICATE_SERIES_NAME_ERROR_MESSAGE,
    LOW_SAMPLE_PARTIAL_RELEASABLE_WARNING_CONTENT,
    ReportRunDisabledTips,
    ReportValidationResultMessages,
    ReportValidationResultWarningMessages,
    SampleRangeValidationMessages,
} from '@/constants/report.constants';
import { useMarketContext } from '@/contexts/MarketContext';
import { useUser } from '@/contexts/UserContext';
import { useMarketService } from '@/services/market.service';
import { debounce } from 'lodash';
import MessageBox from '@/components/MessageBox/MessageBox';
import { safeStringEquals } from '@/utils';

export type RunConfigSummaryStepProps = {
    title?: ReactNode;
    className?: string;
    onNext?: () => void;
    onEnableScheduleReport?: () => void;
    enabled?: boolean;
    sku?: string;
    busy?: boolean;
    busyNextText?: string;
    runConfigOptions?: RunConfigOptions[];
    id?: ConfigOptionsType;
    exclude?: boolean;
    isEditScheduleMode?: boolean;
    scheduledRunConfig?: ReportRunConfig;
    seriesName?: string;
    onChangeSeriesName?: (seriesInput: string) => void;
};

const baseClassName = 'run-config-summary-step';

export const ReportRunConfigSummaryStep = React.forwardRef<WithUid, RunConfigSummaryStepProps>(
    (
        {
            className,
            onNext,
            onEnableScheduleReport,
            title = 'Review And Run',
            sku,
            enabled,
            runConfigOptions,
            busy,
            busyNextText,
            id,
            exclude,
            isEditScheduleMode,
            scheduledRunConfig,
            seriesName,
            onChangeSeriesName,
            ...props
        },
        ref
    ) => {
        const { validateRunConfig, validateOshRunConfig, checkDuplicateSeries } = useTenantApi();
        const stepRef = useRef<any>();
        const {
            company,
            accessibleMasterCompanies,
            customCharacteristics,
            features: { enableLimitedMarketSubscription },
        } = useUser();
        const { activeUid } = useContext(WizardContext);
        const { value: runConfig, onChange: onContextChange } = useContext(ReportRunConfigWizardContext);
        const { allMarkets } = useMarketContext();
        const { validateRunConfigForSku } = useReportRunService();
        const { getCachedMarketNodeByName } = useMarketService();
        const [validating, setValidating] = useState(false);
        const [validationResult, setValidationResult] = useState<ReportRunValidationResult | null>(null);
        const [validationMessage, setValidationMessage] = useState<WizardStepMessageOptions | undefined>();
        const [validatingSeriesName, setValidatingSeriesName] = useState<boolean>(false);
        const [seriesNameError, setSeriesNameError] = useState<string>('');
        const [beyondThreeYearsDataWarning, setBeyondThreeYearsDataWarning] = useState<boolean>(false);
        const lastValidatedConfig = useRef<string>('');
        const { datatype } = runConfig;

        const resultsValidationWarningMessage = useMemo(() => {
            switch (datatype) {
                case 'omni':
                    return ReportValidationResultWarningMessages.omni;
                case 'osh':
                    return ReportValidationResultWarningMessages.osh;
                case 'cps':
                    return ReportValidationResultWarningMessages.cps;
                case 'rms':
                    return ReportValidationResultWarningMessages.rms;
                default:
                    return DEFAULT_REPORT_VALIDATION_RESULT_WARNING_MESSAGE;
            }
        }, [datatype]);

        const hasRunConfigChanged = (): boolean => {            
            if (isEditScheduleMode && scheduledRunConfig) {
                const initialValueHash = hashObject(scheduledRunConfig!, { excludeKeys: (key) => key === 'disabledMarketNode' });
                const valueHash = hashObject(runConfig!, { excludeKeys: (key) => key === 'disabledMarketNode' });
                return !safeStringEquals(initialValueHash, valueHash); // return true if the values have changed
            }
        
            return true; // in other modes, we assume that values have changed
        };

        const getDisabledTip = (): string => {
            if (validating) return ReportRunDisabledTips.validating_selections;
            if (validatingSeriesName) return ReportRunDisabledTips.validating_series_name;
            if (seriesNameError) return seriesNameError;
            if (busy) return ReportRunDisabledTips.report_generating;
            if (!hasRunConfigChanged()) {
                return ReportRunDisabledTips.no_config_change;
            }
            return ReportRunDisabledTips.see_errors_above;
        };

        const disableNext: boolean | undefined = validating || validatingSeriesName || Boolean(seriesNameError) || !validationResult?.canRun || busy || !hasRunConfigChanged();
        
        const nextText: string = isEditScheduleMode
            ? 'Apply'
            : !busy
            ? 'Generate Report'
            : busyNextText ?? 'Running...';
        const nextIconType: string | undefined =
            validating || busy ? 'busy' : validationResult?.canRun && hasRunConfigChanged() ? 'ok' : 'error';

        // this is required to allow multiple refs to the step.  needs the dependency array or will cause infinite loop
        useImperativeHandle(ref, () => stepRef.current, []);
        /**
         * api call to fetch if data set is present or not
         * returns boolean
         */
        const checkIfReportDataIsValid = async () => {
            if (!runConfig) return;
            const comparableRunConfig = JSON.stringify(runConfig);

            // don't bother revalidating the config if it hasn't changed
            if (lastValidatedConfig.current === comparableRunConfig) return;

            lastValidatedConfig.current = comparableRunConfig;
            setBeyondThreeYearsDataWarning(false);
            
            // before bothering to hit the server, make sure all of the required value are set.
            const validationError: boolean | ReportRunValidationError = validateRunConfigForSku(
                sku,
                runConfig,
                allMarkets,
                customCharacteristics
            );

            if (validationError && typeof validationError === 'object') {
                // TODO: Keeping the older error messages for Omni because of time constraint and different handling, will need future ticket to work on it
                switch (datatype) {
                    case 'omni':
                        if (validationError?.omniError) {
                            generateErrorMessage(ReportValidationResultMessages.default);
                        }
                        break;
                    case 'osh': // Todo:- Frontend validation will be introduced later on
                        break;
                    default:
                        handleValidationError(validationError); // Handle errors accordingly
                        break;
                }
            }

            try {
                setValidating(true);
                setValidationMessage(undefined);
                
                let validationResult: ReportRunValidationResult;

                if (datatype === 'osh') {
                    validationResult = await validateOshRunConfig(
                        'subscription',
                        sku as string,
                        runConfig as ReportRunConfig
                    );
                } else {
                    validationResult = await validateRunConfig(
                        'subscription',
                        sku as string,
                        runConfig as ReportRunConfig
                    );
                }

                if (validationResult.hasOwnProperty('range')) {
                    // '>150' (for some CPS reports, perhaps others) and any unmapped range clear the message
                    setValidationMessage(SampleRangeValidationMessages[validationResult.range!] ?? undefined);

                    // This is under BYZ-13828 when some selections are non releasable then we have to show this warning message
                    // There could be a possibility that range includes '>75', '75-150', '>150' but brandsWithTripsLessThan75 or mrktsWithTripsLessThan75 is not present
                    if (
                        ['>75', '75-150', '>150'].includes(validationResult.range!) &&
                        (validationResult?.brandsWithTripsLessThan75?.length ||
                            validationResult?.mrktsWithTripsLessThan75?.length)
                    ) {
                        setValidationMessage({
                            type: 'warning',
                            content: LOW_SAMPLE_PARTIAL_RELEASABLE_WARNING_CONTENT,
                        });
                    }
                } else if (!validationResult.canRun) {
                    setValidationMessage({
                        type: 'error',
                        content: resultsValidationWarningMessage
                    });
                } else {
                    setValidationMessage(undefined);
                }

                setBeyondThreeYearsDataWarning(validationResult.hasReachedMaxDateRange ?? false);
                setValidationResult(validationResult); //checkReportValidation(validateReportData));
            } catch (error: unknown) {
                handleErrorFromResponse(error);
            } finally {
                setValidating(false);
            }
        };

        useEffect(() => {
            if (activeUid === stepRef.current?.uid) {
                checkIfReportDataIsValid();
            }
        }, [activeUid]);

        const handleNext = (): boolean => {
            onNext?.();
            return false; // Making it false because there is one more step after this so, don't want to navigate on clicking Generate Report or Apply button
        };

        // Function to return error message based on market validation
        const generateMarketError = (): string => {
            const matchedMarkets = allMarkets.filter((mrkt) =>
                runConfig?.markets?.some((runMarket) => runMarket.key === mrkt.key)
            );

            if (runConfig?.markets?.length === 1) {
                const cachedMarket = getCachedMarketNodeByName(matchedMarkets[0].name);

                // Accessible market case
                let isMasterCompanyNotAccessible =
                    enableLimitedMarketSubscription &&
                    !accessibleMasterCompanies.includes(matchedMarkets[0].masterCompany!);
                if (isMasterCompanyNotAccessible) {
                    return ReportValidationResultMessages.market_not_accessible;
                }

                // Prior approval case
                if (cachedMarket?.hasApproval === false) {
                    return ReportValidationResultMessages.not_prior_approval;
                }

                // Premium market removal case
                let unPurchased =
                    cachedMarket?.isPremium === true &&
                    !company?.purchasedMarketKeys?.includes(cachedMarket?.key as string);
                if (unPurchased) {
                    return ReportValidationResultMessages.not_rev_share;
                }
            }

            // Multi market error scenario
            return ReportValidationResultMessages.market_not_found_multi;
        };

        const generateCategoryError = (): string => {
            if (runConfig?.metadata?.excludedCategories?.length > 0) {
                return runConfig?.categories?.length! > 0
                    ? ReportValidationResultMessages.category_subscription_not_found_multi
                    : ReportValidationResultMessages.category_subscription_not_found;
            }

            return ReportValidationResultMessages.category_subscription_not_found;
        };

        const handleValidationError = (validationError: ReportRunValidationError) => {
            const { categoryError, marketError, marketNotFoundError, customCharError } = validationError;

            if (categoryError) {
                return generateErrorMessage(generateCategoryError());
            }
            if (marketNotFoundError) {
                return generateErrorMessage(
                    runConfig?.markets?.length! > 1
                        ? ReportValidationResultMessages.market_not_found_multi
                        : ReportValidationResultMessages.market_not_found
                );
            }
            if (marketError) {
                return generateErrorMessage(generateMarketError());
            }
            if (customCharError) {
                return generateErrorMessage(ReportValidationResultMessages.custom_char_not_found);
            }

            // Default error message if no specific errors are found
            return generateErrorMessage(ReportValidationResultMessages.default);
        };

        const generateErrorMessage = (content: string) => {
            setValidationMessage({
                type: 'error',
                content,
            });
            setValidationResult({ canRun: false });
        };

        const handleErrorFromResponse = (error: unknown) => {
            const typedError = error as ErrorWithCodeResponse;
            if (
                typedError.code === ReportValidationResultMessages.category_subscription_not_found &&
                runConfig?.categories?.length! > 0
            ) {
                typedError.code = ReportValidationResultMessages.category_subscription_not_found_multi;
            }

            if (
                [
                    ReportValidationResultMessages.not_prior_approval,
                    ReportValidationResultMessages.not_rev_share,
                    ReportValidationResultMessages.market_not_accessible,
                    ReportValidationResultMessages.market_not_found,
                ].includes(typedError.code!)
            ) {
                typedError.code = ReportValidationResultMessages.market_not_found_multi;
            }

            setValidationMessage({
                type: 'error',
                content: <p>{ReportValidationResultMessages[typedError.code ?? 'default']}</p>,
            });
        };

        const debounceCheckDuplicates = useCallback(
            debounce(async (seriesName: string) => {
                const value = seriesName;

                setSeriesNameError(''); // clear immediately
                onChangeSeriesName?.(value);

                if (!value) return;

                setValidatingSeriesName(true);

                try {
                    const { exists } = await checkDuplicateSeries({
                        name: value,
                        type: 'scheduled',
                    });

                    setSeriesNameError(exists ? DUPLICATE_SERIES_NAME_ERROR_MESSAGE : '');
                } catch (error) {
                    console.error('Error checking duplicate series name:', error);
                } finally {
                    setValidatingSeriesName(false);
                }
            }, 500),
            []
        );

        const handleSeriesNameChange = ({ target }) => {
            setSeriesNameError('');

            const value = target.value.trim();
            onChangeSeriesName?.(value);
            debounceCheckDuplicates(value);
        }

        return (
            <WizardStep
                className={classnames(baseClassName, className)}
                byzRef={stepRef}
                enabled={enabled}
                message={validationMessage}
                title={title}
                id={id}
                exclude={exclude}
            >
                <WizardHeader>
                    <h1 className={'report-run-config-wizard__step-title'}>{title}</h1>
                </WizardHeader>
                <WizardContent>
                    {beyondThreeYearsDataWarning && (
                        <MessageBox type="warning" content={BEYOND_THREE_YEARS_DATA_WARNING_MESSAGE} />
                    )}
                    <ReportRunConfigSummary 
                        runConfig={runConfig} 
                        runConfigOptions={runConfigOptions} 
                        validationResult={validationResult!} 
                    />
                </WizardContent>

                <footer className={`${baseClassName}__step-actions`}>
                    {/* @ts-ignore */}
                    <ByzzerInput
                        label={'Add Series Name:'}
                        name="seriesName"
                        disabled={false}
                        className={`${baseClassName}__manual-series-name`}
                        value={seriesName}
                        onChange={handleSeriesNameChange}
                        placeholder={'optional'}
                    />

                    {/* Need to add omni as well once W/E logic is implemented for Omni report schedule */}
                    {/* If this is the case of EditSchedule then only Apply button will be rendered */}
                    {/* Currently schedule report is only available for relative time period and not for omni and osh */}
                    {['relative'].includes(runConfig.timePeriod?.type) && !isEditScheduleMode && (
                        <ByzzerButton
                            disabled={disableNext}
                            type='negative'
                            disabledTip={getDisabledTip()}
                            onClick={onEnableScheduleReport}
                        >
                            Schedule For Later
                        </ByzzerButton>
                    )}

                    <ByzzerButton
                        disabled={disableNext}
                        disabledTip={getDisabledTip()}
                        onClick={handleNext}
                        label={nextText}
                        iconType={nextIconType}
                    />
                </footer>
            </WizardStep>
        );
    }
);

export default ReportRunConfigSummaryStep;
